import './landerImage.scss';

import { ReactNode, useState } from 'react';

import { useDelayedLoading } from '../../store/apiStore/hooks/useDelayedLoading';
import useLeadChannelStore from '../../store/leadChannelStore/useLeadChannelStore';
import { locator } from '../../testing/e2e-locator/locator';
import { LanderImageProps } from './types/LanderImageProps';

const SKELETON_MIN_VISIBLE_MS = 1700;

export const LanderImage = ({
  alt,
  defaultImage,
}: LanderImageProps): ReactNode => {
  const {
    computed: { customLanderImageURL },
  } = useLeadChannelStore();

  const [loadedImageURL, setLoadedImageURL] = useState<string | null>(null);
  const [brokenImageURL, setBrokenImageURL] = useState<string | null>(null);

  const isCustomImage =
    Boolean(customLanderImageURL) && brokenImageURL !== customLanderImageURL;

  const isSkeletonVisible = useDelayedLoading(
    isCustomImage && loadedImageURL !== customLanderImageURL,
    SKELETON_MIN_VISIBLE_MS,
  );

  const figureClassName = [
    'lander-image',
    isCustomImage && 'lander-image-custom',
  ]
    .filter(Boolean)
    .join(' ');

  const handleImageError = (): void => {
    if (isCustomImage) {
      setBrokenImageURL(customLanderImageURL);
    }
  };

  const handleImageLoad = (): void => {
    if (isCustomImage) {
      setLoadedImageURL(customLanderImageURL);
    }
  };

  return (
    <figure
      className={figureClassName}
      data-testid={locator.components.landerImage.root}
    >
      {isCustomImage ? (
        <div className="lander-image-frame">
          {isSkeletonVisible && (
            <span
              className="lander-image-skeleton skeleton-loading-sweep"
              data-testid={locator.components.landerImage.skeleton}
            />
          )}

          <img
            alt={alt}
            className="lander-image-media"
            data-testid={locator.components.landerImage.image}
            src={customLanderImageURL}
            style={{ visibility: isSkeletonVisible ? 'hidden' : 'visible' }}
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
        </div>
      ) : (
        <img
          alt={alt}
          data-testid={locator.components.landerImage.image}
          src={defaultImage}
        />
      )}
    </figure>
  );
};
